'use client';

import { AlertCircle, RefreshCw, SearchX } from 'lucide-react';
import { cn } from '@/lib/utils';

/** Skeleton do hero (logo + título) — evita espaço vazio enquanto a logo carrega. */
function FairtradeHeroSkeleton() {
  return (
    <section className="relative z-30 overflow-hidden bg-[#030023]" aria-hidden>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#000b6a]/40 via-transparent to-[#030023]" />
      <div className="relative z-10 mx-auto max-w-7xl px-6 pb-6 pt-32 sm:pb-8 md:pb-8 md:pt-40">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-1.5 md:gap-2">
          <div className="h-44 w-44 shrink-0 animate-pulse rounded-full bg-sky-500/10 ring-1 ring-sky-400/15 sm:h-52 sm:w-52 md:h-60 md:w-60" />
          <div className="min-w-0 flex-1 space-y-3 sm:-ml-1 md:-ml-2">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="h-10 w-56 animate-pulse rounded-xl bg-sky-500/10 sm:h-12 sm:w-72" />
              <div className="h-9 w-24 animate-pulse rounded-full bg-sky-500/10" />
            </div>
            <div className="h-4 max-w-xl animate-pulse rounded bg-sky-500/10" />
            <div className="h-4 max-w-lg animate-pulse rounded bg-sky-500/10" />
          </div>
        </div>
      </div>
    </section>
  );
}

export function FairtradeLoadingSkeleton({ isDark = false }: { isDark?: boolean }) {
  return (
    <div className="space-y-0" aria-busy="true" aria-label="Carregando preços">
      <FairtradeHeroSkeleton />
      <div className="mx-auto max-w-7xl space-y-4 px-6 py-8">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={`cat-${i}`}
              className="h-28 animate-pulse rounded-2xl bg-[#030023]/90 ring-1 ring-sky-400/10"
            />
          ))}
        </div>
        <div
          className={cn(
            'overflow-hidden rounded-2xl border',
            isDark ? 'border-blue-900/50 bg-[#01012e]' : 'border-gray-100 bg-white'
          )}
        >
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className={cn(
                'flex items-center gap-3 border-b px-4 py-3 last:border-0',
                isDark ? 'border-blue-900/40' : 'border-gray-50'
              )}
            >
              <div
                className={cn(
                  'h-10 w-10 animate-pulse rounded-lg',
                  isDark ? 'bg-blue-950' : 'bg-gray-100'
                )}
              />
              <div className="flex-1 space-y-2">
                <div
                  className={cn(
                    'h-3.5 w-40 animate-pulse rounded',
                    isDark ? 'bg-blue-950' : 'bg-gray-100'
                  )}
                />
                <div
                  className={cn(
                    'h-3 w-24 animate-pulse rounded',
                    isDark ? 'bg-blue-950/70' : 'bg-gray-50'
                  )}
                />
              </div>
              <div
                className={cn(
                  'hidden h-5 w-16 animate-pulse rounded sm:block',
                  isDark ? 'bg-blue-950' : 'bg-gray-100'
                )}
              />
              <div
                className={cn(
                  'hidden h-5 w-20 animate-pulse rounded md:block',
                  isDark ? 'bg-blue-950' : 'bg-gray-100'
                )}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function FairtradeErrorState({
  onRetry,
  isDark = false,
}: {
  onRetry: () => void;
  isDark?: boolean;
}) {
  return (
    <div className="mx-auto flex max-w-lg flex-col items-center px-6 py-16 text-center">
      <div
        className={cn(
          'mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border',
          isDark
            ? 'border-rose-500/30 bg-rose-500/10 text-rose-300'
            : 'border-rose-200 bg-rose-50 text-rose-600'
        )}
      >
        <AlertCircle className="h-7 w-7" aria-hidden />
      </div>
      <h2
        className={cn(
          'font-poppins text-xl font-bold',
          isDark ? 'text-sky-50' : 'text-gray-900'
        )}
      >
        Não foi possível carregar os preços
      </h2>
      <p
        className={cn(
          'mt-2 font-inter text-sm leading-relaxed',
          isDark ? 'text-blue-100/65' : 'text-gray-600'
        )}
      >
        Não conseguimos acessar os dados do Fairtrade no momento. Tente novamente em alguns
        instantes.
      </p>
      <button
        type="button"
        onClick={onRetry}
        className={cn(
          'mt-6 inline-flex items-center gap-2 rounded-xl px-4 py-2.5',
          'font-poppins text-sm font-semibold',
          'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/50',
          isDark
            ? 'border border-sky-400/30 bg-sky-500/15 text-sky-100 hover:bg-sky-500/25'
            : 'bg-[#030023] text-sky-100 hover:bg-[#061a4a]'
        )}
      >
        <RefreshCw className="h-4 w-4" aria-hidden />
        Tentar novamente
      </button>
    </div>
  );
}

export function FairtradeEmptyState({
  onClear,
  isDark = false,
}: {
  onClear: () => void;
  isDark?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center rounded-2xl border border-dashed px-6 py-14 text-center',
        isDark
          ? 'border-blue-800/70 bg-[#01012e]/80'
          : 'border-gray-200 bg-white'
      )}
    >
      <div
        className={cn(
          'mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border',
          isDark
            ? 'border-blue-800 bg-[#030023] text-sky-300/70'
            : 'border-gray-200 bg-gray-50 text-gray-500'
        )}
      >
        <SearchX className="h-7 w-7" aria-hidden />
      </div>
      <h2
        className={cn(
          'font-poppins text-lg font-bold',
          isDark ? 'text-sky-50' : 'text-gray-900'
        )}
      >
        Nenhum power encontrado
      </h2>
      <p
        className={cn(
          'mt-2 max-w-md font-inter text-sm leading-relaxed',
          isDark ? 'text-blue-100/65' : 'text-gray-600'
        )}
      >
        Não encontramos nenhum power correspondente aos filtros selecionados.
      </p>
      <button
        type="button"
        onClick={onClear}
        className={cn(
          'mt-5 inline-flex items-center rounded-xl border px-4 py-2.5',
          'font-poppins text-sm font-semibold transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/40',
          isDark
            ? 'border-sky-400/30 bg-sky-500/15 text-sky-100 hover:bg-sky-500/25'
            : 'border-sky-200 bg-sky-50 text-sky-900 hover:bg-sky-100'
        )}
      >
        Limpar filtros
      </button>
    </div>
  );
}
